import { SavedVideo, VideoMetadata } from '../video.model';

interface R2Config {
  bucketName: string;
  accountId: string;
  accessKeyId: string;
  secretAccessKey: string;
  region?: string;
}

class CloudVideoStorage {
  private config: R2Config | null = null;
  private baseUrl: string = '';

  async init(): Promise<void> {
    this.config = {
      bucketName: process.env.REACT_APP_R2_BUCKET_NAME || 'tsworld-videos',
      accountId: process.env.REACT_APP_R2_ACCOUNT_ID || '',
      accessKeyId: process.env.REACT_APP_R2_ACCESS_KEY_ID || '',
      secretAccessKey: process.env.REACT_APP_R2_SECRET_ACCESS_KEY || '',
      region: process.env.REACT_APP_R2_REGION || 'auto'
    };
    
    this.baseUrl = `https://${this.config.accountId}.r2.cloudflarestorage.com/${this.config.bucketName}`;
  }

  async saveVideo(video: SavedVideo): Promise<void> {
    if (!this.config) await this.init();
    
    try {
      const videoKey = `videos/${video.id}.webm`;
      const metadataKey = `metadata/${video.id}.json`;
      
      if (video.blob) {
        await this.uploadToR2(videoKey, video.blob);
      }
      
      const metadata: VideoMetadata = {
        id: video.id,
        name: video.name,
        description: video.description,
        duration: video.duration,
        recordedAt: video.recordedAt,
        r2Key: videoKey
      };
      
      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      await this.uploadToR2(metadataKey, metadataBlob);
      
    } catch (error) {
      console.error('Error saving video to R2:', error);
      throw new Error('Failed to save video to cloud storage');
    }
  }

  async getVideos(): Promise<VideoMetadata[]> {
    if (!this.config) await this.init();
    
    try {
      const metadataList = await this.listR2Objects('metadata/');
      const videos: VideoMetadata[] = [];
      
      for (const key of metadataList) {
        try {
          const metadata = await this.downloadFromR2(key);
          if (metadata) {
            const videoData = JSON.parse(metadata);
            videos.push({
              ...videoData,
              recordedAt: new Date(videoData.recordedAt)
            });
          }
        } catch (error) {
          console.error(`Error loading metadata for ${key}:`, error);
        }
      }
      
      return videos.sort((a, b) => 
        new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
      );
    } catch (error) {
      console.error('Error loading videos from R2:', error);
      throw new Error('Failed to load videos from cloud storage');
    }
  }

  async getVideoBlob(id: string): Promise<Blob | null> {
    if (!this.config) await this.init();
    
    try {
      const videoKey = `videos/${id}.webm`;
      const response = await fetch(`${this.baseUrl}/${videoKey}`, {
        method: 'GET',
        headers: this.getAuthHeaders('GET', videoKey)
      });
      
      if (response.ok) {
        return await response.blob();
      }
      return null;
    } catch (error) {
      console.error('Error loading video blob from R2:', error);
      return null;
    }
  }

  async updateVideo(id: string, updates: Partial<VideoMetadata>): Promise<void> {
    if (!this.config) await this.init();
    
    try {
      const metadataKey = `metadata/${id}.json`;
      const existingMetadata = await this.downloadFromR2(metadataKey);
      
      if (existingMetadata) {
        const videoData = JSON.parse(existingMetadata);
        const updatedMetadata = { ...videoData, ...updates };
        
        const metadataBlob = new Blob([JSON.stringify(updatedMetadata)], { type: 'application/json' });
        await this.uploadToR2(metadataKey, metadataBlob);
      } else {
        throw new Error('Video not found');
      }
    } catch (error) {
      console.error('Error updating video in R2:', error);
      throw new Error('Failed to update video');
    }
  }

  async deleteVideo(id: string): Promise<void> {
    if (!this.config) await this.init();
    
    try {
      const videoKey = `videos/${id}.webm`;
      const metadataKey = `metadata/${id}.json`;
      
      await Promise.all([
        this.deleteFromR2(videoKey),
        this.deleteFromR2(metadataKey)
      ]);
    } catch (error) {
      console.error('Error deleting video from R2:', error);
      throw new Error('Failed to delete video');
    }
  }

  async getStorageUsage(): Promise<{ used: number; quota: number }> {
    return { used: 0, quota: 0 };
  }

  async clearAllVideos(): Promise<void> {
    if (!this.config) await this.init();
    
    try {
      const [videoKeys, metadataKeys] = await Promise.all([
        this.listR2Objects('videos/'),
        this.listR2Objects('metadata/')
      ]);
      
      const deletePromises = [...videoKeys, ...metadataKeys].map(key => 
        this.deleteFromR2(key)
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error clearing all videos from R2:', error);
      throw new Error('Failed to clear all videos');
    }
  }

  private async uploadToR2(key: string, blob: Blob): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${key}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders('PUT', key),
        'Content-Type': blob.type || 'application/octet-stream'
      },
      body: blob
    });
    
    if (!response.ok) {
      throw new Error(`Failed to upload to R2: ${response.statusText}`);
    }
  }

  private async downloadFromR2(key: string): Promise<string | null> {
    const response = await fetch(`${this.baseUrl}/${key}`, {
      method: 'GET',
      headers: this.getAuthHeaders('GET', key)
    });
    
    if (response.ok) {
      return await response.text();
    }
    return null;
  }

  private async deleteFromR2(key: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${key}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders('DELETE', key)
    });
    
    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete from R2: ${response.statusText}`);
    }
  }

  private async listR2Objects(prefix: string): Promise<string[]> {
    const response = await fetch(`${this.baseUrl}?list-type=2&prefix=${prefix}`, {
      method: 'GET',
      headers: this.getAuthHeaders('GET', '')
    });
    
    if (response.ok) {
      const text = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');
      const keys = Array.from(xmlDoc.getElementsByTagName('Key')).map(
        element => element.textContent || ''
      );
      return keys.filter(key => key.startsWith(prefix));
    }
    return [];
  }

  private getAuthHeaders(method: string, key: string): Record<string, string> {
    if (!this.config) return {};
    
    const date = new Date().toISOString();
    return {
      'Authorization': `AWS4-HMAC-SHA256 Credential=${this.config.accessKeyId}`,
      'X-Amz-Date': date,
      'X-Amz-Content-Sha256': 'UNSIGNED-PAYLOAD'
    };
  }
}

export const cloudVideoStorage = new CloudVideoStorage();
